import { useEffect, useState } from 'react'
import { useCelebrations, useHireDates, useMyBirthday, useSaveHireDate, useSaveMyBirthday } from '../api/queries'
import type { Celebration, UserRef } from '../api/types'
import { useModuleEnabled } from '../navigation'
import { Avatar, Card } from './ui'

const MONTHS = ['januar', 'februar', 'marts', 'april', 'maj', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'december']

/** "I dag", "I morgen" eller "fredag 3. oktober". */
function dayLabel(iso: string) {
  const date = new Date(`${iso}T12:00:00`)
  const today = new Date()
  today.setHours(12, 0, 0, 0)
  const diff = Math.round((date.getTime() - today.getTime()) / 86_400_000)
  if (diff === 0) return 'I dag'
  if (diff === 1) return 'I morgen'
  return date.toLocaleDateString('da-DK', { weekday: 'long', day: 'numeric', month: 'long' })
}

export function celebrationText(c: Celebration) {
  return c.kind === 'birthday'
    ? `${c.user.displayName} har fødselsdag`
    : `${c.user.displayName} har ${c.years} års jubilæum`
}

/**
 * Fødselsdage og jubilæer i dag og de næste 7 dage (beslutning 117) - øverst på intranettets Nyheder.
 * Skjult, når der ikke er nogen.
 */
export function CelebrationsBox() {
  const enabled = useModuleEnabled()('intranet_celebrations')
  const { data } = useCelebrations(7, enabled)
  if (!enabled || !data || data.length === 0) return null

  return (
    <Card title="🎂 Fødselsdage og jubilæer">
      <div className="stack" style={{ gap: 8 }}>
        {data.map((c) => (
          <div key={`${c.kind}-${c.user.id}-${c.date}`} className="row" style={{ gap: 10 }}>
            <Avatar user={c.user} size={32} />
            <div className="small">
              <strong>{c.kind === 'birthday' ? '🎂' : '🎉'} {celebrationText(c)}</strong>
              <div className="tiny muted">{dayLabel(c.date)}</div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  )
}

/** Én linje på forsiden, når nogen har fødselsdag eller jubilæum i dag (beslutning 117). */
export function TodayCelebrationLine() {
  const moduleEnabled = useModuleEnabled()
  const enabled = moduleEnabled('intranet_celebrations')
  const { data } = useCelebrations(1, enabled)
  if (!enabled || !data || data.length === 0) return null

  return (
    <div className="map-notice" style={{ marginBottom: 16 }}>
      {data.map((c, i) => (
        <span key={`${c.kind}-${c.user.id}`}>
          {i > 0 && ' · '}
          {c.kind === 'birthday' ? '🎂' : '🎉'} {celebrationText(c)} i dag
        </span>
      ))}
      {' '}- sig tillykke!
    </div>
  )
}

/** Ens egen fødselsdag på profilen - dag og måned, aldrig år. */
export function BirthdayCard() {
  const { data } = useMyBirthday()
  const save = useSaveMyBirthday()
  const [month, setMonth] = useState('')
  const [day, setDay] = useState('')
  const [show, setShow] = useState(true)
  const [message, setMessage] = useState<string | null>(null)

  useEffect(() => {
    if (!data) return
    setMonth(data.month ? String(data.month) : '')
    setDay(data.day ? String(data.day) : '')
    setShow(data.show)
  }, [data])

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setMessage(null)
    try {
      await save.mutateAsync({ month: month ? Number(month) : null, day: day ? Number(day) : null, show })
      setMessage('Gemt.')
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Fødselsdagen kunne ikke gemmes.')
    }
  }

  return (
    <Card title="🎂 Min fødselsdag">
      <form className="stack" onSubmit={submit}>
        <div className="small muted">
          Så kan kollegerne sige tillykke. Kun dag og måned - året gemmes ikke.
        </div>
        <div className="row" style={{ gap: 8 }}>
          <select style={{ maxWidth: 90 }} value={day} onChange={(e) => setDay(e.target.value)} aria-label="Dag">
            <option value="">Dag</option>
            {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
              <option key={d} value={d}>{d}.</option>
            ))}
          </select>
          <select style={{ maxWidth: 160 }} value={month} onChange={(e) => setMonth(e.target.value)} aria-label="Måned">
            <option value="">Måned</option>
            {MONTHS.map((name, i) => (
              <option key={name} value={i + 1}>{name}</option>
            ))}
          </select>
        </div>
        <label className="row small" style={{ gap: 8 }}>
          <input type="checkbox" style={{ width: 'auto' }} checked={show} onChange={(e) => setShow(e.target.checked)} />
          Vis den for kollegerne
        </label>
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          <button className="btn btn--sm btn--primary" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Gemmer ...' : 'Gem'}
          </button>
          {(month || day) && (
            <button className="btn btn--sm" type="button" onClick={() => { setMonth(''); setDay('') }}>
              Fjern
            </button>
          )}
          {message && <span className="tiny muted">{message}</span>}
        </div>
      </form>
    </Card>
  )
}

/** Ansættelsesdatoen i telefonlisten - kun for kontoret (beslutning 117). */
export function HireDateInput({ user, value }: { user: UserRef; value: string | null | undefined }) {
  const save = useSaveHireDate()
  const [draft, setDraft] = useState(value ?? '')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => setDraft(value ?? ''), [value])

  async function commit(next: string) {
    if (next === (value ?? '')) return
    setError(null)
    try {
      await save.mutateAsync({ userId: user.id, hireDate: next || null })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunne ikke gemme.')
    }
  }

  return (
    <label className="tiny muted" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      Ansat
      <input
        type="date"
        style={{ maxWidth: 150, padding: '4px 6px' }}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => void commit(e.target.value)}
      />
      {error && <span className="warn">{error}</span>}
    </label>
  )
}

/** Ansættelsesdatoerne slået op pr. bruger. */
export function useHireDateMap(enabled: boolean) {
  const { data } = useHireDates(enabled)
  return new Map((data ?? []).map((h) => [h.userId, h.hireDate]))
}
